import { forwardRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Select } from '../forms/Select';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  className?: string;
}

const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [20, 50, 100];

const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      onPageChange,
      onItemsPerPageChange,
      itemsPerPageOptions = DEFAULT_ITEMS_PER_PAGE_OPTIONS,
      className,
    },
    ref
  ) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const pages = useMemo(() => {
      const items: (number | 'ellipsis')[] = [];

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          items.push(i);
        }
      } else {
        items.push(1);

        if (currentPage > 3) {
          items.push('ellipsis');
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) {
          if (!items.includes(i)) {
            items.push(i);
          }
        }

        if (currentPage < totalPages - 2) {
          items.push('ellipsis');
        }

        if (!items.includes(totalPages)) {
          items.push(totalPages);
        }
      }

      return items;
    }, [currentPage, totalPages]);

    if (totalPages <= 1) {
      return (
        <div ref={ref} className={cn('flex items-center gap-4 max-sm:flex-wrap max-sm:gap-y-2', className)}>
          <span className="text-sm text-neutral-500">
            {totalItems > 0 ? `${totalItems} resultat${totalItems > 1 ? 's' : ''}` : 'Aucun resultat'}
          </span>
          {onItemsPerPageChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">Afficher</span>
              <div className="w-20">
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(value) => onItemsPerPageChange(Number(value))}
                  options={itemsPerPageOptions.map((option) => ({
                    value: option.toString(),
                    label: option.toString(),
                  }))}
                  allowEmpty={false}
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        // Telephone : le compteur, le choix du nombre par page et les numeros
        // de page passent a la ligne au lieu de deborder.
        className={cn('flex items-center justify-between gap-4 max-sm:flex-wrap max-sm:gap-y-2', className)}
      >
        <div className="flex items-center gap-4 max-sm:flex-wrap max-sm:gap-y-2">
          <span className="text-sm text-neutral-500">
            {startItem} a {endItem} sur {totalItems} resultats
          </span>
          {onItemsPerPageChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">Afficher</span>
              <div className="w-20">
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(value) => onItemsPerPageChange(Number(value))}
                  options={itemsPerPageOptions.map((option) => ({
                    value: option.toString(),
                    label: option.toString(),
                  }))}
                  allowEmpty={false}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
              'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
            )}
            aria-label="Page precedente"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {pages.map((page, index) =>
            page === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                className="flex h-8 w-8 items-center justify-center text-neutral-400"
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={cn(
                  'flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  page === currentPage
                    ? 'bg-primary-500 text-white'
                    : 'hover:bg-neutral-100 text-dark-500'
                )}
              >
                {page}
              </button>
            )
          )}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
              'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
            )}
            aria-label="Page suivante"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';

export { Pagination };
